import type React from 'react';
import type { PeriodFilterType } from '../types/reporting.types';

interface PeriodSelectorProps {
  selectedYear: number;
  selectedPeriod: PeriodFilterType;
  onYearChange: (year: number) => void;
  onPeriodChange: (period: PeriodFilterType) => void;
  isLoading?: boolean;
}

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  selectedYear,
  selectedPeriod,
  onYearChange,
  onPeriodChange,
  isLoading = false,
}) => {
  // Generate year options (current year and previous 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const periodOptions: {
    value: PeriodFilterType;
    label: string;
    description: string;
  }[] = [
    { value: 'H1', label: 'H1', description: 'January - June' },
    { value: 'H2', label: 'H2', description: 'July - December' },
    { value: 'BOTH', label: 'Both', description: 'Full Year (H1 + H2)' },
  ];

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        {/* Year Selector */}
        <div className="flex flex-col">
          <label htmlFor="year-select" className="text-sm font-medium text-gray-700 mb-1">
            Year
          </label>
          <select
            id="year-select"
            value={selectedYear}
            onChange={(e) => onYearChange(parseInt(e.target.value, 10))}
            disabled={isLoading}
            className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Period Selector */}
        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">Period</label>
          <div className="flex gap-2">
            {periodOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => onPeriodChange(option.value)}
                disabled={isLoading}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 disabled:cursor-not-allowed ${
                  selectedPeriod === option.value
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:hover:bg-gray-100'
                }`}
                title={option.description}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {periodOptions.find((p) => p.value === selectedPeriod)?.description}
          </div>
        </div>

        {/* Loading Indicator */}
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
            Loading report...
          </div>
        )}
      </div>

      {/* Selected Period Summary */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="text-sm text-gray-600">
          <span className="font-medium">Selected Period:</span>{' '}
          {selectedPeriod === 'BOTH'
            ? `Full Year ${selectedYear} (H1 + H2)`
            : `${selectedPeriod} ${selectedYear}`}
          {selectedPeriod === 'H1' && ' (January - June)'}
          {selectedPeriod === 'H2' && ' (July - December)'}
        </div>
      </div>
    </div>
  );
};

export default PeriodSelector;
